/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-magic-numbers */
import {
  scaleLinear,
  scaleSqrt,
  scaleLog,
  ScaleLinear,
  ScalePower,
  ScaleLogarithmic
} from 'd3-scale';
import merge from 'deepmerge';
import WordCloudSeries, {
  Weight,
  WordCloudSeriesOption,
  WordCloudDataItemOption
} from '../extension/series/dvWordCloud/WordCloudSeries';

export function getScale(
  type: 'linear' | 'sqrt' | 'log',
  domain: [number, number],
  range: [number, number]
) {
  let scale:
    | ScaleLinear<number, number>
    | ScalePower<number, number>
    | ScaleLogarithmic<number, number>;

  switch (type) {
    case 'linear':
      scale = scaleLinear().domain(domain).range(range);
      break;
    case 'sqrt':
      scale = scaleSqrt().domain(domain).range(range);
      break;
    case 'log':
      scale = scaleLog().domain(domain).range(range);
      break;
    default:
      throw new Error('Invalid scale type');
  }

  return scale;
}

export function formatPaddingOrMargin(data: number | number[]) {
  if (typeof data === 'number') {
    return [data, data, data, data];
  }
  if (Array.isArray(data)) {
    const formatData = [...data];
    while (formatData.length < 4) {
      formatData.push(data[formatData.length % data.length]);
    }
    return formatData;
  }
  return [0, 0, 0, 0];
}

// 根据字号字重计算外接矩形
export function getSymbolSize(
  word: string,
  fontSize: number,
  fontWeight: number,
  padding: number | number[]
) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas context is not supported');
  }

  context.font = `${fontWeight} ${fontSize}px sans-serif`;
  const textMetrics = context.measureText(word);
  const { width } = textMetrics;
  const height = fontSize;
  const paddingTemp = formatPaddingOrMargin(padding);
  const symbolWidth = width + paddingTemp[1] + paddingTemp[3];
  const symbolHeight = height + paddingTemp[0] + paddingTemp[2];
  const symbolWidthFormat = parseFloat(symbolWidth.toFixed(2));
  const symbolHeightFormat = parseFloat(symbolHeight.toFixed(2));
  return [symbolWidthFormat, symbolHeightFormat];
}

export function getFormatData(option: WordCloudSeriesOption) {
  const optionTemp = merge(WordCloudSeries.defaultOption, option, {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    arrayMerge: (destinationArray, sourceArray) => sourceArray
  });
  const data = optionTemp.data || [];
  const formatData: WordCloudDataItemOption[] = [];

  // 计算极值获取定义域
  let dataMin = Infinity;
  let dataMax = -Infinity;
  data.forEach(item => {
    if (Array.isArray(item)) {
      const weight = item[1] as Weight;
      if (typeof weight !== 'number' || Number.isNaN(weight)) {
        return;
      }
      dataMin = Math.min(dataMin, weight);
      dataMax = Math.max(dataMax, weight);
    } else {
      const { value = [] } = item;
      const weight = value[1] as Weight;
      if (typeof weight !== 'number' || Number.isNaN(weight)) {
        return;
      }
      dataMin = Math.min(dataMin, weight);
      dataMax = Math.max(dataMax, weight);
    }
  });

  // 根据比例尺处理字号字重
  const { fontSizeScale, fontSizeRange, fontWeightScale, fontWeightRange } = optionTemp;
  // 如果用户只传入比例尺，则默认直接传入权重
  // 如果用户传入的是一个函数，则需要把整个数据项传入

  let fontSizeScaleTemp;
  let fontWeightScaleTemp;

  if (typeof fontSizeScale !== 'function') {
    fontSizeScaleTemp = getScale(fontSizeScale, [dataMin, dataMax], fontSizeRange);
  }
  if (typeof fontWeightScale !== 'function') {
    fontWeightScaleTemp = getScale(fontWeightScale, [dataMin, dataMax], fontWeightRange);
  }

  data.forEach(item => {
    if (Array.isArray(item)) {
      const weight = item[1] as Weight;
      if (typeof weight !== 'number' || Number.isNaN(weight)) {
        return;
      }
      const fontSize =
        typeof fontSizeScale !== 'function' ? fontSizeScaleTemp(weight) : fontSizeScale(item);
      const fontWeight =
        typeof fontWeightScale !== 'function' ? fontWeightScaleTemp(weight) : fontWeightScale(item);
      // 保留俩位小数，通过减少计算精度节省计算量
      const fontSizeFormat = parseFloat(fontSize.toFixed(2));
      const fontWeightFormat = parseFloat(fontWeight.toFixed(2));
      formatData.push({
        value: item,
        label: {
          fontSize: fontSizeFormat,
          fontWeight: fontWeightFormat
        }
      });
    } else {
      const { value = [] } = item;
      const weight = value[1] as Weight;
      if (typeof weight !== 'number' || Number.isNaN(weight)) {
        return;
      }
      const fontSize =
        typeof fontSizeScale !== 'function' ? fontSizeScaleTemp(weight) : fontSizeScale(item);
      const fontWeight =
        typeof fontWeightScale !== 'function' ? fontWeightScaleTemp(weight) : fontWeightScale(item);
      // 保留俩位小数，通过减少计算精度节省计算量
      const fontSizeFormat = parseFloat(fontSize.toFixed(2));
      const fontWeightFormat = parseFloat(fontWeight.toFixed(2));
      formatData.push(
        merge(
          {
            label: {
              fontSize: fontSizeFormat,
              fontWeight: fontWeightFormat
            }
          },
          item
        )
      );
    }
  });

  // 合并全局和个体影响布局的配置项
  formatData.forEach(item => {
    const globalItemStyle = {
      padding: optionTemp.layout?.padding,
      margin: optionTemp.layout?.margin
    };
    item.itemStyle = merge(globalItemStyle, item.itemStyle || {});
  });
  // 根据字号、字重、padding计算symbolSize
  formatData.forEach(item => {
    const { value } = item;
    const word = value[0] as string;
    const fontSize = item.label.fontSize as number;
    const fontWeight = item.label.fontWeight as number;
    const padding = item.itemStyle.padding as number;
    const symbolSize = getSymbolSize(word, fontSize, fontWeight, padding);
    item.symbolSize = symbolSize;
  });

  return formatData;
}
